/**
 * Mapbox helpers for Explore bank-label taps.
 * Discovers label layers by id prefix (no casino-id allow lists) and reads
 * visible label text from known GeoJSON properties.
 */

/** Known GeoJSON properties that hold visible bank / amenity label text. */
export const BANK_LABEL_TEXT_PROPERTIES = ['name', 'autocad_text_string'] as const;

export type BankLabelFeatureProps = Record<string, unknown> | null | undefined;

/**
 * Visible bank/amenity text from a Mapbox feature's properties.
 * Prefers `name` / `autocad_text_string` (what the symbol layer paints).
 */
export function bankLabelTextFromProperties(
  props: BankLabelFeatureProps,
  preferredProperty?: string | null
): string | null {
  if (!props) return null;
  const preferred = String(preferredProperty ?? '').trim();
  if (preferred) {
    const forced = String(props[preferred] ?? '').trim();
    if (forced) return forced;
  }
  for (const key of BANK_LABEL_TEXT_PROPERTIES) {
    const v = String(props[key] ?? '').trim();
    if (v) return v;
  }
  return null;
}

type MapLike = {
  getStyle?: () => { layers?: Array<{ id?: string }> } | undefined;
  getLayer?: (id: string) => unknown;
};

/**
 * Layer ids that may hold tappable bank labels on the current style:
 * - `bankIdLabelLayer*` (AutoCAD symbol labels: SSC/ACH/RWC/JJC, …)
 * - `bankIdLayer` alone when it is the symbol label layer (LOTC/Eureka)
 *
 * Does not hardcode casino ids — whatever layers exist on the live style.
 */
export function existingBankLabelLayerIds(map: MapLike | null | undefined): string[] {
  if (!map) return [];
  const layers = map.getStyle?.()?.layers;
  if (Array.isArray(layers) && layers.length > 0) {
    const ids: string[] = [];
    for (const layer of layers) {
      const id = String(layer?.id ?? '');
      if (!id) continue;
      if (id.startsWith('bankIdLabelLayer')) {
        ids.push(id);
        continue;
      }
      // LOTC/Eureka: sole bank symbol layer is plain `bankIdLayer` (not *Label*).
      if (id === 'bankIdLayer' && Boolean(map.getLayer?.(id))) {
        ids.push(id);
      }
    }
    return ids;
  }
  // Fallback when getStyle is unavailable (tests / stubs).
  const candidates = [
    'bankIdLabelLayerSSC',
    'bankIdLabelLayerACH',
    'bankIdLabelLayerRWC',
    'bankIdLabelLayerJJC',
    'bankIdLabelLayer',
    'bankIdLayer',
  ];
  return candidates.filter((id) => Boolean(map.getLayer?.(id)));
}
